import { Link } from "wouter";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, Clock, HelpCircle } from "lucide-react";
import { useSEO } from "@/lib/seo-utils";

type ReviewStatusVariant = "expired" | "not-found" | "error";

const CONFIG: Record<ReviewStatusVariant, { title: string; message: string; icon: typeof AlertCircle }> = {
  expired: {
    title: "Review Link Expired",
    message: "This review invitation has expired. If you'd still like to share feedback, contact us and we'll help you submit a review.",
    icon: Clock,
  },
  "not-found": {
    title: "Review Not Found",
    message: "We couldn't find a matching stay for this review link. Please check your email or contact us for assistance.",
    icon: HelpCircle,
  },
  error: {
    title: "Something Went Wrong",
    message: "We couldn't load the review form. Please try again later or contact us directly.",
    icon: AlertCircle,
  },
};

interface ReviewStatusPageProps {
  variant: ReviewStatusVariant;
}

export default function ReviewStatusPage({ variant }: ReviewStatusPageProps) {
  const { title, message, icon: Icon } = CONFIG[variant];

  useSEO({
    title,
    robots: "noindex, nofollow",
  });

  return (
    <div className="min-h-screen bg-shell">
      <Header />
      <main id="main-content" className="pt-24 pb-16 sm:pt-28 flex items-center justify-center px-4">
        <Card className="w-full max-w-md rounded-2xl border-sand-deep bg-sand/60 shadow-none">
          <CardContent className="px-5 py-8 sm:px-8 text-center">
            <Icon className="h-12 w-12 text-forest mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-luxury-2xl font-serif font-normal text-forest mb-3">{title}</h1>
            <p className="text-ink-soft font-sans mb-6 leading-relaxed">{message}</p>
            <div className="flex flex-col gap-3">
              <Link href="/contact">
                <Button variant="bronze" className="h-12 w-full">Contact Us</Button>
              </Link>
              <Link href="/">
                <Button variant="outline" className="h-12 w-full border-sand-deep bg-shell">Return Home</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
